import React from "react";
import {
  Box,
  Button,
  Grid,
  Wrap,
  WrapItem,
  Text,
  VStack,
  Badge,
  Flex,
} from "@chakra-ui/react";
import { customWorlds, GameMode, BackgroundType } from "../types";

type GameStartParams = {
  mode: GameMode;
  timed?: boolean;
  worldId?: number;
};

type WorldSelectModalProps = {
  selectedWorldId: number;
  setSelectedWorldId: (worldId: number) => void;
  setGameMode: (gameMode: GameMode) => void;
  beginNewPuzzle: (mode?: GameMode, worldId?: number) => void;
  onStartGame: (params?: GameStartParams) => void;
  onBack: () => void;
};

// Preview colors for world cards (use representative colors for animated backgrounds)
const getPreviewBackground = (backgroundType?: BackgroundType): string => {
  switch (backgroundType) {
    // Animated backgrounds
    case BackgroundType.calmWaves:
      return "linear-gradient(135deg, #1a4a6e 0%, #2d7da8 100%)";
    case BackgroundType.forestCanopy:
      return "linear-gradient(135deg, #1a3a1a 0%, #2d5a2d 100%)";
    case BackgroundType.desertDunes:
      return "linear-gradient(135deg, #c4a574 0%, #e8d4a8 100%)";
    case BackgroundType.portalRealm:
      return "linear-gradient(135deg, #2a1a4a 0%, #6a3a9a 100%)";
    case BackgroundType.hauntedHouse:
      return "linear-gradient(135deg, #1a1a2e 0%, #3a3a5e 100%)";
    case BackgroundType.vaporwaveCity:
      return "linear-gradient(135deg, #ff6b9d 0%, #c44569 50%, #6a0572 100%)";
    case BackgroundType.alienVillage:
      return "linear-gradient(135deg, #1a2a1a 0%, #3a5a4a 100%)";
    case BackgroundType.paintSplat:
      return "linear-gradient(135deg, #fef3e2 0%, #fde2e4 50%, #e2eff1 100%)";
    case BackgroundType.magicWorld:
      return "linear-gradient(135deg, #1a1a4a 0%, #2a2a6a 100%)";
    case BackgroundType.jungle:
      return "linear-gradient(135deg, #0d3b0d 0%, #1a5a1a 100%)";
    case BackgroundType.technology:
      return "linear-gradient(135deg, #0a1628 0%, #1a3a5a 100%)";
    case BackgroundType.magicPortal:
      return "linear-gradient(135deg, #2d0a45 0%, #6a2a8a 100%)";
    case BackgroundType.warehouse:
      return "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)";
    // Solid colors
    case BackgroundType.solidBlack:
      return "#1a1a1a";
    case BackgroundType.solidGold:
      return "linear-gradient(135deg, #FFC841 0%, #FFD966 100%)";
    case BackgroundType.solidOcean:
      return "linear-gradient(135deg, #1a5a8a 0%, #2783D8 100%)";
    case BackgroundType.solidPurple:
    case BackgroundType.solidMagenta:
      return "linear-gradient(135deg, #6a0572 0%, #9900C3 100%)";
    case BackgroundType.solidRed:
      return "linear-gradient(135deg, #6a0000 0%, #980000 100%)";
    case BackgroundType.solidOrange:
      return "linear-gradient(135deg, #c44020 0%, #FF5733 100%)";
    case BackgroundType.solidGreen:
    case BackgroundType.solidForest:
      return "linear-gradient(135deg, #0d3b0d 0%, #1B780F 100%)";
    default:
      return "#2a2a2a";
  }
};

// Determine if text should be light or dark based on background
const shouldUseLightText = (backgroundType?: BackgroundType): boolean => {
  const lightBackgrounds = [
    BackgroundType.desertDunes,
    BackgroundType.paintSplat,
    BackgroundType.solidGold,
  ];
  return !lightBackgrounds.includes(backgroundType as BackgroundType);
};

const WorldSelectModal: React.FC<WorldSelectModalProps> = ({
  selectedWorldId,
  setSelectedWorldId,
  setGameMode,
  beginNewPuzzle,
  onStartGame,
  onBack,
}) => {
  const handleWorldSelect = (worldId: number) => {
    setSelectedWorldId(worldId);
    setGameMode(GameMode.worldSelect);
    beginNewPuzzle(GameMode.worldSelect, worldId);
    onStartGame({ mode: GameMode.worldSelect, worldId });
  };

  const getWorldTitle = (index: number) => {
    const world = customWorlds[index];
    return world.name;
  };

  const getWorldDescription = (index: number) => {
    const world = customWorlds[index];
    return `${world.startingSize}×${world.startingSize} • ${world.startingColors} colors • ${world.effects.length} effects`;
  };

  return (
    <Flex direction="column" w="100%" maxH={{ base: "70vh", md: "80vh" }}>
      {/* Header - Fixed */}
      <Flex justify="space-between" align="center" px={1} pb={3} flexShrink={0}>
        <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color="white">
          Select World
        </Text>
        <Button size="sm" onClick={onBack}>
          Back
        </Button>
      </Flex>

      {/* Scrollable World List */}
      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        px={1}
        mb={3}
        sx={{
          touchAction: "pan-y",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "2px",
          },
        }}
      >
        <Grid templateColumns="repeat(1, 1fr)" gap={2}>
          {customWorlds.map((world, index) => {
            const isSelected = selectedWorldId === index;
            const useLightText = shouldUseLightText(world.backgroundType);
            const textColor = useLightText ? "white" : "gray.800";
            const badgeScheme = useLightText ? "whiteAlpha" : "blackAlpha";

            return (
              <Box
                key={index}
                p={{ base: 3, md: 4 }}
                borderWidth={3}
                borderRadius="lg"
                cursor="pointer"
                borderColor={isSelected ? "blue.400" : "transparent"}
                background={getPreviewBackground(world.backgroundType)}
                color={textColor}
                onClick={() => setSelectedWorldId(index)}
                transition="all 0.2s"
                boxShadow={isSelected ? "0 0 0 2px rgba(66, 153, 225, 0.6)" : "md"}
                _hover={{
                  borderColor: isSelected ? "blue.400" : "blue.300",
                  transform: "translateY(-2px)",
                  boxShadow: "lg",
                }}
                _active={{
                  transform: "scale(0.98)",
                }}
              >
                <VStack align="start" spacing={1}>
                  <Text
                    fontWeight="bold"
                    fontSize={{ base: "md", md: "lg" }}
                    textShadow={useLightText ? "0 1px 2px rgba(0,0,0,0.3)" : "none"}
                  >
                    {getWorldTitle(index)}
                  </Text>
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    opacity={0.9}
                    textShadow={useLightText ? "0 1px 2px rgba(0,0,0,0.2)" : "none"}
                  >
                    {getWorldDescription(index)}
                  </Text>
                  <Wrap spacing={1} pt={1}>
                    {world.effects.slice(0, 3).map((effect, effectIndex) => (
                      <WrapItem key={effectIndex}>
                        <Badge
                          fontSize={{ base: "2xs", md: "xs" }}
                          colorScheme={badgeScheme}
                          variant="solid"
                          textTransform="capitalize"
                        >
                          {effect.replace(/-/g, " ")}
                        </Badge>
                      </WrapItem>
                    ))}
                    {world.effects.length > 3 && (
                      <WrapItem>
                        <Badge
                          fontSize={{ base: "2xs", md: "xs" }}
                          colorScheme={badgeScheme}
                          variant="solid"
                        >
                          +{world.effects.length - 3} more
                        </Badge>
                      </WrapItem>
                    )}
                  </Wrap>
                </VStack>
              </Box>
            );
          })}
        </Grid>
      </Box>

      {/* Play Button - Fixed at bottom */}
      <Box flexShrink={0} pt={2} borderTop="1px solid" borderColor="whiteAlpha.200">
        <Button
          colorScheme="blue"
          size={{ base: "md", md: "lg" }}
          w="100%"
          h={{ base: "50px", md: "56px" }}
          onClick={() => handleWorldSelect(selectedWorldId)}
          isDisabled={selectedWorldId === undefined}
        >
          Play {getWorldTitle(selectedWorldId)}
        </Button>
      </Box>
    </Flex>
  );
};

export default WorldSelectModal;
